import React, { useState, useEffect, useRef } from 'react';
import '../styles/VibrationDashboard.css';

const ARC = Math.PI * 90; // ≈ 283
const VIB_WARN = 4;
const VIB_CRIT = 7;

function vibStateOf(vib) {
  if (vib == null) return { state: 'unknown', label: '—', color: '#7e8299' };
  if (vib >= VIB_CRIT) return { state: 'alert', label: 'Crítico', color: '#dc2626' };
  if (vib >= VIB_WARN) return { state: 'warn',  label: 'Advertencia', color: '#d97706' };
  return { state: 'ok', label: 'Normal', color: '#059669' };
}

function useCountUp(value, duration = 600) {
  const safe = value == null || Number.isNaN(Number(value)) ? null : Number(value);
  const [display, setDisplay] = useState(safe ?? 0);
  const prev = useRef(safe ?? 0);
  
  useEffect(() => {
    if (safe == null) return;
    const start = prev.current;
    const end = safe;
    if (start === end) { setDisplay(end); return; }
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(start + (end - start) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else prev.current = end;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [safe, duration]);
  return safe == null ? null : display;
}

const VibrationDashboard = ({ value, max24h, mean24h }) => {
  const animatedV = useCountUp(value, 700);
  const st = vibStateOf(value);
  
  // Matemáticas de la aguja y colores
  const v = Math.max(0, Math.min(10, animatedV || 0));
  const pct = v / 10;
  const gPct = Math.min(pct, 0.4);
  const aPct = Math.max(0, Math.min(pct - 0.4, 0.3));
  const rPct = Math.max(0, pct - 0.7);
  const gLen = (gPct / 0.4) * ARC * 0.4;
  const aLen = (aPct / 0.3) * ARC * 0.3;
  const rLen = (rPct / 0.3) * ARC * 0.3;
  const aOff = -(ARC * 0.4 - gLen);
  const rOff = -(ARC * 0.7 - (gLen + aLen));
  const angle = -90 + pct * 180;
  const transition = 'all 700ms cubic-bezier(.4,0,.2,1)';

  return (
    <div className="dash-card-body">
      <div className="dash-card-hdr">
        <div className="dash-card-title">
          <h2>Nivel de Vibración Actual</h2>
          <span className="dash-tag ta">WISE-2410</span>
        </div>
      </div>
      
      <div className="dash-gauge-section">
        <svg width="200" height="115" viewBox="0 0 220 130" style={{ overflow: 'visible' }}>
          <path d="M 20 115 A 90 90 0 0 1 200 115" fill="none" stroke="var(--metric-line)" strokeWidth="16" strokeLinecap="round" />
          <path d="M 20 115 A 90 90 0 0 1 200 115" fill="none" stroke="#059669" strokeWidth="16" strokeLinecap="round" strokeDasharray={`${gLen} ${ARC - gLen}`} style={{ transition }} />
          <path d="M 20 115 A 90 90 0 0 1 200 115" fill="none" stroke="#d97706" strokeWidth="16" strokeLinecap="round" strokeDasharray={`${aLen} ${ARC - aLen}`} strokeDashoffset={aOff} style={{ transition }} />
          <path d="M 20 115 A 90 90 0 0 1 200 115" fill="none" stroke="#dc2626" strokeWidth="16" strokeLinecap="round" strokeDasharray={`${rLen} ${ARC - rLen}`} strokeDashoffset={rOff} style={{ transition }} />
          
          <g transform={`rotate(${angle} 110 115)`} style={{ transition: 'transform 700ms cubic-bezier(.4,0,.2,1)' }}>
            <line x1="110" y1="115" x2="110" y2="32" stroke="var(--text-0)" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="110" cy="115" r="7" fill="var(--text-0)" />
            <circle cx="110" cy="115" r="3.5" fill="var(--bg-1)" />
          </g>
          
          {[ { x: 16, y: 130, label: '0' }, { x: 55, y: 42, label: '2.5' }, { x: 110, y: 22, label: '5' }, { x: 165, y: 42, label: '7.5' }, { x: 204, y: 130, label: '10' }].map(t => (
            <text key={t.label} x={t.x} y={t.y} fontFamily="JetBrains Mono, monospace" fontSize="9" fill="var(--text-2)" textAnchor="middle">{t.label}</text>
          ))}
        </svg>

        <div className="dash-gauge-numbers">
          <span className="dash-gauge-big">{animatedV == null ? '—' : animatedV.toFixed(2)}</span>
          <span className="dash-gauge-unit">mm/s</span>
          <div>
            <span className="dash-gauge-state" style={{ background: `color-mix(in srgb, ${st.color} 12%, transparent)`, color: st.color, border: `1px solid color-mix(in srgb, ${st.color} 35%, transparent)`, transition: 'all 380ms ease' }}>
              <span className="dash-state-dot" style={{ background: st.color, transition: 'background 380ms' }}></span>
              Estado: {st.label}
            </span>
          </div>
        </div>

        <div className="dash-spark-row">
          <div className="dash-spark-card">
            <div className="dash-spark-lbl">Máx 24h</div>
            <div className="dash-spark-val" style={{ color: 'rgb(217, 119, 6)' }}><span>{max24h ? max24h.toFixed(2) : '—'} mm/s</span></div>
          </div>
          <div className="dash-spark-card">
            <div className="dash-spark-lbl">Media 24h</div>
            <div className="dash-spark-val" style={{ color: 'rgb(37, 99, 235)' }}><span>{mean24h ? mean24h.toFixed(2) : '—'} mm/s</span></div>
          </div>
          <div className="dash-spark-card">
            <div className="dash-spark-lbl">Frec. dom.</div>
            <div className="dash-spark-val">49.8 Hz</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VibrationDashboard;